import Layout from '@components/Layout';
import Loading from '@components/Loading';
import { ROUTE } from '@config/navigation';
import { useCheckoutConfigContext } from '@contexts/CheckoutConfig';
import { StepperProvider } from '@contexts/Stepper';
import { yupResolver } from '@hookform/resolvers/yup';
import Donation from '@routes/Donation';
import Payment from '@routes/Payment';
import Supporter from '@routes/Supporter';
import ThankYouPage from '@routes/ThankYouPage';
import { DONATION_FREQUENCY, FormValues, LEGAL_PERSON, STEP } from '@types';
import { convertStepToRoute } from '@utils/convertRoutes';
import createFormValuesSchema from '@utils/createFormValuesSchema';
import { FC, useEffect, useMemo } from 'react';
import { FormProvider, useForm, UseFormProps } from 'react-hook-form';
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

const FormRouter: FC = () => {
  const { checkout, checkoutData } = useCheckoutConfigContext();

  const formOptions: UseFormProps<FormValues> = useMemo(() => {
    const defaultValues: Partial<FormValues> = {
      legalPerson: LEGAL_PERSON.INDIVIDUAL,
      amount:
        // Trim trailing zeros
        (checkout.campaign.default_amount &&
          (checkout.campaign.default_amount % 1 == 0
            ? Math.round(checkout.campaign.default_amount)
            : checkout.campaign.default_amount)) ||
        10,
      frequency: checkout.campaign.has_one_off
        ? DONATION_FREQUENCY.ONEOFF
        : DONATION_FREQUENCY.MONTHLY,
      paymentMethod: checkout.enabled_payment_methods[0]?.type,
      paymentMethodId: checkout.enabled_payment_methods[0]?.id,
      acceptedPrivacyFields: [],
    };

    return {
      mode: 'onChange',
      defaultValues: { ...defaultValues, ...checkoutData }, // merge default values with init values from config
      resolver: yupResolver(createFormValuesSchema(checkout)),
    };
  }, [checkout]);

  const methods = useForm<FormValues>(formOptions);

  return (
    <FormProvider {...methods}>
      <StepperProvider initStep={checkout.step as STEP}>
        <form style={{ width: '100%', height: '100%' }}>
          <Routes>
            <Route index element={<CheckoutConfigRedirect />} />
            <Route path="/" element={<Layout />}>
              <Route path={ROUTE.DONATION} element={<Donation />} />
              <Route path={ROUTE.SUPPORTER} element={<Supporter />} />
              <Route path={ROUTE.PAYMENT} element={<Payment />} />
            </Route>
            <Route path={ROUTE.THANK_YOU} element={<ThankYouPage />} />
          </Routes>
        </form>
      </StepperProvider>
    </FormProvider>
  );
};

const CheckoutConfigRedirect: FC = () => {
  const { checkout } = useCheckoutConfigContext();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => location.search, []);

  useEffect(() => {
    navigate((convertStepToRoute(checkout.step) || ROUTE.DONATION) + params, {
      replace: true,
    });
  }, [checkout.step, navigate]);

  return <Loading />;
};

const CheckoutConfigLoader: FC = () => {
  const { status, error } = useCheckoutConfigContext();
  const landingPath = useMemo(() => location.pathname, []);

  switch (status) {
    case 'idle':
    case 'loading':
      return <Loading />;

    case 'error':
      throw error;

    case 'success':
      return (
        <BrowserRouter basename={landingPath}>
          <FormRouter />
        </BrowserRouter>
      );
  }
};

export default CheckoutConfigLoader;
